import React, { useRef, useEffect, useState } from "react";
import { CheckIcon, CloseIcon } from "../../../constants/Icons";

const ProductNoteModal = ({
  onClose,
  onSave,
  initialNote = "",
  noteSuggests,
}) => {
  const textareaRef = useRef(null);
  const modalContentRef = useRef(null); // <- changed: ref the whole modal content
  const suggestionNoteRef = useRef([]);
  const [note, setNote] = useState(initialNote);
  const [noteSuggest, setNoteSuggest] = useState(noteSuggests || {});
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [filteredNoteSuggestions, setFilteredNoteSuggestions] = useState(
    noteSuggests || {},
  );
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Keep flat suggestions order with General first (as you already had)
  const flatSuggestions = [
    ...(filteredNoteSuggestions["General"]
      ? filteredNoteSuggestions["General"]
      : []),
    ...Object.entries(filteredNoteSuggestions)
      .filter(([category]) => category !== "General")
      .flatMap(([_, items]) => items),
  ];

  // Reset suggestion refs when suggestions change (avoid stale refs)
  useEffect(() => {
    suggestionNoteRef.current = [];
  }, [filteredNoteSuggestions]);

  // Handle save
  const handleSave = () => {
    const trimmed = note.trim();
    if (!trimmed) return;
    // If you want to add into General automatically, do it here (optional)
    const allNotes = Object.values(noteSuggest).flat();
    let updated = { ...noteSuggest };
    if (!allNotes.includes(trimmed)) {
      updated["General"] = updated["General"]
        ? [...updated["General"], trimmed]
        : [trimmed];
    }
    setNoteSuggest(updated);
    setFilteredNoteSuggestions(updated);

    onSave(trimmed);
    onClose();
  };

  // Handle textarea changes & filter suggestions
  const handleChange = (e) => {
    const value = e.target.value;
    setNote(value);

    if (!value.trim()) {
      setFilteredNoteSuggestions(noteSuggest);
      setShowNotePopup(true);
      setHighlightedIndex(-1);
      return;
    }

    const filtered = {};
    Object.entries(noteSuggest).forEach(([category, items]) => {
      const matched = items.filter((item) =>
        item.toLowerCase().includes(value.toLowerCase()),
      );
      if (matched.length > 0) filtered[category] = matched;
    });

    setFilteredNoteSuggestions(filtered);
    setShowNotePopup(Object.keys(filtered).length > 0);
    setHighlightedIndex(-1);
  };

  // Handle keyboard events
  const handleKeyDown = (e) => {
    if (showNotePopup && flatSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < flatSuggestions.length - 1 ? prev + 1 : 0,
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : flatSuggestions.length - 1,
        );
        return;
      }
      if (e.key === "Enter" && !e.ctrlKey) {
        e.preventDefault();
        if (highlightedIndex >= 0) {
          const selectedItem = flatSuggestions[highlightedIndex];
          setNote(selectedItem);
          setShowNotePopup(false);
          setHighlightedIndex(-1);
          return;
        }
      }
    }

    if (e.key === "Enter" && e.ctrlKey) {
      handleSave();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  // Scroll suggestion into view when highlighted changes
  useEffect(() => {
    if (highlightedIndex >= 0 && suggestionNoteRef.current[highlightedIndex]) {
      suggestionNoteRef.current[highlightedIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedIndex]);

  // Close popup when clicking outside the modal content (not just the suggestion container)
  useEffect(() => {
    const handleClickOutside = (e) => {
      // If click is outside the whole modal content -> close suggestions
      if (
        modalContentRef.current &&
        !modalContentRef.current.contains(e.target)
      ) {
        setShowNotePopup(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto focus on mount
  useEffect(() => {
    if (!initialNote) textareaRef.current?.focus();
  }, [initialNote]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEsc);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);

  return (
    <div>
      <div className="modal-backdrop show"></div>
      <div
        className="modal show"
        tabIndex="0"
        aria-modal="true"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-md">
          {/* <-- Move ref to modal-content so footer buttons are considered "inside" */}
          <div
            className="modal-content"
            ref={modalContentRef}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
          >
            <div className="modal-header">
              <h6 className="modal-title">Add Note</h6>
              <button
                type="button"
                onClick={() => {
                  setShowNotePopup(false);
                  onClose();
                }}
                className="btn-close"
                aria-label="Close"
              ></button>
            </div>

            <div
              className="modal-body"
              style={{
                minHeight:
                  showNotePopup && flatSuggestions.length > 0
                    ? "300px"
                    : "auto",
              }}
            >
              <div className="position-relative">
                <textarea
                  ref={textareaRef}
                  className="form-control"
                  rows="3"
                  maxLength="500"
                  value={note}
                  onChange={handleChange}
                  onFocus={() => {
                    setFilteredNoteSuggestions(noteSuggest);
                    setShowNotePopup(true);
                    setHighlightedIndex(-1);
                  }}
                  placeholder="Type..."
                />
                <div className="text-end small mt-1 text-muted">
                  {note.length}/500
                </div>

                {showNotePopup && flatSuggestions.length > 0 && (
                  <ul className="suggestions-list">
                    {[
                      ...(filteredNoteSuggestions["General"]
                        ? [["General", filteredNoteSuggestions["General"]]]
                        : []),
                      ...Object.entries(filteredNoteSuggestions).filter(
                        ([category]) => category !== "General",
                      ),
                    ].map(([category, items], catIndex) => (
                      <li key={catIndex}>
                        <div className="suggestion-item highlighted-title">
                          {category}
                        </div>
                        <ul className="suggestions-list-inside">
                          {items.map((item, idx) => {
                            const flatIndex = flatSuggestions.indexOf(item);
                            return (
                              <li
                                key={`${catIndex}-${idx}`}
                                ref={(el) =>
                                  (suggestionNoteRef.current[flatIndex] = el)
                                }
                                className={`suggestion-item ${
                                  highlightedIndex === flatIndex
                                    ? "highlighted"
                                    : ""
                                }`}
                                onMouseDown={() => {
                                  setNote(item);
                                  setShowNotePopup(false);
                                  setHighlightedIndex(-1);
                                }}
                              >
                                {item}
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="modal-footer d-flex justify-content-between">
              <button
                onClick={() => {
                  setShowNotePopup(false); // close suggestions immediately
                  onClose();
                }}
                type="button"
                className="btn btn-outline-secondary"
              >
                <CloseIcon iClass={"fs-15"}/> Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowNotePopup(false); // close suggestions immediately
                  handleSave();
                }}
              >
                <CheckIcon/> Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductNoteModal;
